import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientsQuery, useDeleteClientMutation } from '../../api/useClientsQueries';
import type { ClientUser } from '../../api/clientsApi';

function getName(c: ClientUser) {
  return (
    [c.firstName, c.lastName].filter(Boolean).join(' ') ||
    (typeof c.companyName === 'string' ? c.companyName : '') ||
    ''
  );
}

/** Owns the client list's search/status filters, pagination, delete-confirm state, and derived stats. */
export function useClientsPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const listQ = useClientsQuery({ page, limit: 12 });
  const deleteMut = useDeleteClientMutation();

  const clients: ClientUser[] = useMemo(() => {
    const d = listQ.data?.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.users)) return d.users;
    return [];
  }, [listQ.data]);

  const stats = useMemo(() => {
    let verified = 0;
    let pending = 0;
    for (const c of clients) {
      const s = String(c.status || '').toUpperCase();
      if (s === 'VERIFIED') verified += 1;
      else pending += 1;
    }
    return { total: clients.length, verified, pending };
  }, [clients]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      const s = String(c.status || '').toUpperCase();
      if (statusFilter === 'VERIFIED' && s !== 'VERIFIED') return false;
      if (statusFilter === 'PENDING' && s === 'VERIFIED') return false;
      if (!q) return true;
      return [getName(c), c.email, c.mobileNumber, c.companyName, c.businessCategory]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [clients, search, statusFilter]);

  const pagination = listQ.data?.data?.pagination || listQ.data?.pagination;
  const totalPages = pagination?.totalPages || pagination?.pages || listQ.data?.totalPages || 1;
  const total = pagination?.totalCount || pagination?.total || listQ.data?.total || clients.length;
  const hasFilters = Boolean(search.trim()) || statusFilter !== 'All';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('All');
  };

  return {
    navigate,
    page,
    setPage,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    deleteId,
    setDeleteId,
    isPending: listQ.isPending,
    clients,
    stats,
    filtered,
    totalPages,
    total,
    hasFilters,
    clearFilters,
    isDeleting: deleteMut.isPending,
    confirmDelete: () => {
      if (!deleteId) return;
      deleteMut.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
    },
  };
}
